import React from 'react';
import {
  WarningIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  FileXlsIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  AlertContainer as Alert,
  AlertDescription,
  AlertTitle,
} from 'src/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { Badge } from 'src/components/ui/badge';
import { useParseCsv } from 'src/api/rpc/hooks/rebates/useHistoricalImport';
import type {
  GetAgreementImportContextResponse,
  ParseHistoricalCsvResponse,
} from '@erp/rpc';
import { NON_REBATEABLE_TURNOVER_TYPES } from '@erp/rpc';

interface Step2ParsePreviewProps {
  agreementContext: GetAgreementImportContextResponse;
  year: number;
  onComplete: (csvData: ParseHistoricalCsvResponse) => void;
  onBack: () => void;
  onError: (error: string) => void;
  isLoading: boolean;
  error?: string;
}

function Step2ParsePreview({
  agreementContext,
  year,
  onComplete,
  onBack,
  onError,
  isLoading,
  error,
}: Step2ParsePreviewProps) {
  const parseMutation = useParseCsv();

  const handleParse = async () => {
    if (!agreementContext.rexelCode) {
      onError('No rexel code available');
      return;
    }

    try {
      const result = await parseMutation.mutateAsync({
        agreementId: agreementContext.agreementId,
        rexelCode: agreementContext.rexelCode,
        year,
      });
      // Don't complete yet - show preview first
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to parse CSV');
    }
  };

  const handleContinue = () => {
    if (parseMutation.data) {
      onComplete(parseMutation.data);
    }
  };

  const csvData = parseMutation.data;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-2">Parse CSV Preview</h2>
        <p className="text-muted-foreground">
          The CSV will be filtered by rexel code{' '}
          <Badge variant="secondary">{agreementContext.rexelCode}</Badge> and
          year <Badge variant="secondary">{year}</Badge>.
        </p>
      </div>

      {/* Parse Button */}
      {!csvData && (
        <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg">
          <FileXlsIcon className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">
            Click to parse and preview the CSV data
          </p>
          <Button onClick={handleParse} disabled={parseMutation.isPending}>
            {parseMutation.isPending ? 'Parsing...' : 'Parse CSV'}
          </Button>
        </div>
      )}

      {/* Error Display */}
      {(error || parseMutation.error) && (
        <Alert variant="destructive">
          <WarningIcon className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error ||
              (parseMutation.error instanceof Error
                ? parseMutation.error.message
                : 'Failed to parse CSV')}
          </AlertDescription>
        </Alert>
      )}

      {/* Results Display */}
      {csvData && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">
                Total Rows in File
              </p>
              <p className="text-2xl font-bold">
                {csvData.totalRowsInFile.toLocaleString()}
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">Filtered Rows</p>
              <p className="text-2xl font-bold text-green-600">
                {csvData.filteredRows.toLocaleString()}
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">Skipped Rows</p>
              <p className="text-2xl font-bold text-muted-foreground">
                {csvData.skippedRows.toLocaleString()}
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">Unique Products</p>
              <p className="text-2xl font-bold">
                {csvData.productCodes.length.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Date Range */}
          <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground">
              Transaction Date Range
            </p>
            <p className="font-medium">
              {csvData.dateRange.earliest} - {csvData.dateRange.latest}
            </p>
          </div>

          {/* Divisions */}
          <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground mb-2">
              Divisions Found
            </p>
            <div className="flex flex-wrap gap-2">
              {csvData.divisions.map((division) => (
                <Badge key={division} variant="outline">
                  {division}
                </Badge>
              ))}
            </div>
          </div>

          {/* Turnover Type Breakdown */}
          {csvData.turnoverTypeBreakdown &&
            csvData.turnoverTypeBreakdown.length > 0 && (
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground mb-3">
                  Turnover Type Breakdown
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {csvData.turnoverTypeBreakdown.map((breakdown) => {
                    const isNonRebateable =
                      NON_REBATEABLE_TURNOVER_TYPES.includes(
                        breakdown.turnoverType,
                      );
                    return (
                      <div
                        key={breakdown.turnoverType}
                        className={`rounded-lg border p-3 ${
                          isNonRebateable ? 'bg-muted/50' : 'bg-green-50/50'
                        }`}
                      >
                        <p className="text-xs font-medium mb-1">
                          {breakdown.turnoverType}
                        </p>
                        <p className="text-lg font-bold">
                          {breakdown.rowCount.toLocaleString()} rows
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {breakdown.productCount} products | $
                          {breakdown.totalValue.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                        <Badge
                          variant={isNonRebateable ? 'secondary' : 'default'}
                          className="mt-2 text-xs"
                        >
                          {isNonRebateable ? 'Non-Rebateable' : 'Rebateable'}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          {/* Product Codes Preview */}
          <div>
            <h3 className="font-medium mb-2">
              Product Codes ({csvData.productCodes.length})
            </h3>
            <div className="rounded-lg border max-h-64 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product Reference</TableHead>
                    <TableHead>Product Name</TableHead>
                    <TableHead>Turnover Type</TableHead>
                    <TableHead className="text-right">Transactions</TableHead>
                    <TableHead className="text-right">Total Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvData.productCodes.slice(0, 20).map((code) => {
                    const isNonRebateable =
                      NON_REBATEABLE_TURNOVER_TYPES.includes(code.turnoverType);
                    return (
                      <TableRow key={code.productReference}>
                        <TableCell className="font-mono text-sm">
                          {code.productReference}
                        </TableCell>
                        <TableCell>{code.product}</TableCell>
                        <TableCell>
                          <Badge
                            variant={isNonRebateable ? 'secondary' : 'outline'}
                            className="text-xs"
                          >
                            {code.turnoverType}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {code.transactionCount.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          $
                          {code.totalValue.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {csvData.productCodes.length > 20 && (
                <div className="p-2 text-center text-sm text-muted-foreground border-t">
                  ... and {csvData.productCodes.length - 20} more
                </div>
              )}
            </div>
          </div>

          {/* Sample Rows */}
          {csvData.sampleRows.length > 0 && (
            <div>
              <h3 className="font-medium mb-2">Sample Rows</h3>
              <div className="rounded-lg border overflow-auto max-h-48">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {Object.keys(csvData.sampleRows[0]).map((key) => (
                        <TableHead key={key} className="whitespace-nowrap">
                          {key}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {csvData.sampleRows.slice(0, 5).map((row, idx) => (
                      <TableRow key={idx}>
                        {Object.values(row).map((value, colIdx) => (
                          <TableCell
                            key={colIdx}
                            className="whitespace-nowrap text-sm"
                          >
                            {value}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!csvData || csvData.filteredRows === 0 || isLoading}
        >
          Continue
          <ArrowRightIcon className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

export default Step2ParsePreview;
